/**
 * OpenRouter Parameter Utilities
 *
 * Converts OpenRouter's supported_parameters array into JSON Schema
 * that the existing parameter merge pipeline understands.
 */

import { exposeLlmSettings, llmSettingsProperties, resolveLlmEndpoint } from '@genieartinc/schemas'

/** Metadata returned from our OpenRouter proxy endpoint */
export interface OpenRouterModelMeta {
  supported_parameters: string[]
  default_parameters: Record<string, unknown>
  context_length: number | null
  max_completion_tokens: number | null
  pricing: { prompt: string; completion: string } | null
}

/** Match a saved endpoint id, including Genie's unpublished seed ids, against the live list. */
export function lookupOpenRouterMeta(
  map: Record<string, OpenRouterModelMeta> | null | undefined,
  modelId: string | undefined,
): OpenRouterModelMeta | null {
  if (!map || !modelId) return null
  return map[modelId] ?? map[resolveLlmEndpoint(modelId)] ?? null
}

/**
 * Convert a model's published parameters into the JSON Schema the settings
 * panel already renders. Only parameters that model publishes are included.
 * Defaults are omitted so an untouched control is not sent as an override.
 */
export function buildParameterSchemaFromOpenRouter(
  meta: OpenRouterModelMeta,
  modelId?: string,
): { properties: Record<string, unknown> } {
  const exposure = exposeLlmSettings({
    provider: 'openrouter',
    modelId,
    discovered: meta.supported_parameters,
    contextLength: meta.context_length,
    maxCompletionTokens: meta.max_completion_tokens,
  })
  return { properties: llmSettingsProperties(exposure) }
}
